import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Shield,
  FileCheck,
  AlertTriangle,
  CheckCircle,
  Clock,
  Eye,
  Edit,
  Trash2
} from 'lucide-react';
import { TabLayout, ContentCard } from '../../components/ui/TabLayout';
import { ResponsiveTable, StatusBadge } from '../../components/ui/table';
import { Button } from '../../components/ui/Button/Button';
import { Filter } from '../../components/ui/Filter';
import { EditDriverModal } from '../../components/modals/EditDriverModal';
import { DeleteDriverDialog } from '../../components/modals/ConfirmationDialog';
import { updateDriver, deleteDriver } from '../../store/driverSlice';
import { useToast } from '../../components/ui/Toast';

export function DriverVerificationPage() {
  const dispatch = useDispatch();
  const { showSuccess, showError } = useToast();
  const { drivers } = useSelector(state => state.drivers);
  const [activeTab, setActiveTab] = useState('pending');
  const [filter, setFilter] = useState('');

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);

  // Action handlers
  const handleEdit = (driver) => {
    setSelectedDriver(driver);
    setShowEditModal(true);
  };

  const handleDelete = (driver) => {
    setSelectedDriver(driver);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async (reason) => {
    if (!selectedDriver) return;

    try {
      const result = await dispatch(deleteDriver(selectedDriver.id, {
        softDelete: true,
        reason: reason || 'Deleted via verification page'
      }));

      if (result.success) {
        showSuccess('Success', `${selectedDriver.personalInfo?.firstName || selectedDriver.name} has been deleted`);
      } else {
        showError('Error', result.error || 'Failed to delete driver');
      }
    } catch (error) {
      showError('Error', 'An unexpected error occurred');
    } finally {
      setShowDeleteDialog(false);
      setSelectedDriver(null);
    }
  };

  const verificationColumns = [
    {
      key: 'name',
      header: 'Driver Name',
      primary: true,
      render: (_, item) => {
        const name = item?.name || `${item?.personalInfo?.firstName || ''} ${item?.personalInfo?.lastName || ''}`.trim();
        const email = item?.email || item?.personalInfo?.email || '';
        return (
          <div>
            <div className="font-medium text-gray-900">{name}</div>
            <div className="text-sm text-gray-500">{email}</div>
          </div>
        );
      }
    },
    {
      key: 'licenseStatus',
      header: 'License',
      render: (value) => (
        <StatusBadge 
          status={value || 'pending'} 
          variant={
            value === 'verified' ? 'success' :
            value === 'expired' ? 'error' :
            value === 'pending' ? 'warning' : 'default'
          } 
        />
      )
    },
    {
      key: 'backgroundCheck',
      header: 'Background Check',
      render: (value) => (
        <StatusBadge 
          status={value || 'pending'} 
          variant={
            value === 'passed' ? 'success' :
            value === 'failed' ? 'error' :
            value === 'pending' ? 'warning' : 'default'
          } 
        />
      )
    },
    {
      key: 'vehicleInspection',
      header: 'Vehicle Inspection',
      render: (value) => (
        <StatusBadge 
          status={value || 'pending'} 
          variant={
            value === 'passed' ? 'success' :
            value === 'failed' ? 'error' :
            value === 'pending' ? 'warning' : 'default'
          } 
        />
      )
    },
    {
      key: 'lastVerified',
      header: 'Last Verified',
      render: (value) => value ? new Date(value).toLocaleDateString() : 'Never'
    }
  ];

  const verificationActions = [
    {
      type: 'button',
      onClick: (item) => console.log('Verify driver:', item),
      label: 'Verify',
      variant: 'primary',
      icon: Shield
    },
    {
      type: 'button',
      onClick: (item) => handleEdit(item),
      label: 'Edit',
      variant: 'secondary',
      icon: Edit
    },
    {
      type: 'button',
      onClick: (item) => handleDelete(item),
      label: 'Delete',
      variant: 'error',
      icon: Trash2
    }
  ];

  const getFilteredDrivers = () => {
    return drivers.filter(driver => {
      const name = driver?.name || `${driver?.personalInfo?.firstName || ''} ${driver?.personalInfo?.lastName || ''}`.trim();
      const email = driver?.email || driver?.personalInfo?.email || '';
      const matchesFilter = name.toLowerCase().includes(filter.toLowerCase()) ||
                           email.toLowerCase().includes(filter.toLowerCase());
      
      switch (activeTab) {
        case 'pending':
          return matchesFilter && (
            driver.licenseStatus === 'pending' ||
            driver.backgroundCheck === 'pending' ||
            driver.vehicleInspection === 'pending'
          );
        case 'verified':
          return matchesFilter && 
                 driver.licenseStatus === 'verified' &&
                 driver.backgroundCheck === 'passed' &&
                 driver.vehicleInspection === 'passed';
        case 'issues':
          return matchesFilter && (
            driver.licenseStatus === 'expired' ||
            driver.backgroundCheck === 'failed' ||
            driver.vehicleInspection === 'failed'
          );
        default:
          return matchesFilter;
      }
    });
  };

  const filteredDrivers = getFilteredDrivers();

  const tabs = [
    { id: 'pending', label: 'Pending Verification', icon: Clock },
    { id: 'verified', label: 'Verified Drivers', icon: CheckCircle },
    { id: 'issues', label: 'Verification Issues', icon: AlertTriangle }
  ];

  const getVerificationStats = () => {
    const pending = drivers.filter(d => 
      d.licenseStatus === 'pending' || 
      d.backgroundCheck === 'pending' || 
      d.vehicleInspection === 'pending'
    ).length;
    
    const verified = drivers.filter(d => 
      d.licenseStatus === 'verified' && 
      d.backgroundCheck === 'passed' && 
      d.vehicleInspection === 'passed'
    ).length;
    
    const issues = drivers.filter(d => 
      d.licenseStatus === 'expired' || 
      d.backgroundCheck === 'failed' || 
      d.vehicleInspection === 'failed'
    ).length;

    return { pending, verified, issues };
  };

  const stats = getVerificationStats();

  return (
    <>
      <TabLayout
        title="Driver Verification"
        subtitle="Manage driver document verification and background checks"
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      >
      {/* Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex-1 max-w-md">
          <Filter
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by name or email..."
          />
        </div>
      </div>

      {/* Verification Table */}
      <ResponsiveTable
        data={filteredDrivers}
        columns={verificationColumns}
        actions={verificationActions}
        emptyState={
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Shield size={48} className="mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              No drivers in {activeTab} verification
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              There are currently no drivers requiring {activeTab} verification.
            </p>
          </div>
        }
      />

      {/* Verification Statistics */}
      <ContentCard title="Verification Overview">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              {stats.pending}
            </div>
            <p className="text-sm text-gray-600 mt-1">Pending Verification</p>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {stats.verified}
            </div>
            <p className="text-sm text-gray-600 mt-1">Fully Verified</p>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">
              {stats.issues}
            </div>
            <p className="text-sm text-gray-600 mt-1">Verification Issues</p>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {drivers.length > 0 ? ((stats.verified / drivers.length) * 100).toFixed(1) : 0}%
            </div>
            <p className="text-sm text-gray-600 mt-1">Verification Rate</p>
          </div>
        </div>
      </ContentCard>

      {/* Quick Actions */}
      <ContentCard title="Verification Actions">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Button variant="primary" className="w-full">
            <FileCheck size={16} className="mr-2" />
            Bulk Verify Documents
          </Button>
          <Button variant="secondary" className="w-full">
            <Shield size={16} className="mr-2" />
            Run Background Checks
          </Button>
          <Button variant="outline" className="w-full">
            <AlertTriangle size={16} className="mr-2" />
            Review Issues
          </Button>
        </div>
      </ContentCard>
      </TabLayout>

      {/* Modals */}
      <EditDriverModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        driver={selectedDriver}
      />

      <DeleteDriverDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={confirmDelete}
        driverName={selectedDriver?.personalInfo?.firstName || selectedDriver?.name || 'Driver'}
        isLoading={false}
      />
    </>
  );
}
